export function findIndexSafety<T>(
  arr: T[],
  predicate: (v: T) => boolean,
  defaultIndex: number = 0,
) {
  const index = arr.findIndex(predicate)

  if (index < 0) {
    return defaultIndex
  }

  return index
}

export const flatten = <T>(list: any, targetProperty: string = '', func?: Function): T[] => {
  const outer = targetProperty ? list[targetProperty] : list

  return outer.reduce((inner: T[], item: any) => {
    if (Array.isArray(item) || Array.isArray(item[targetProperty])) {
      return inner.concat(flatten(item, targetProperty, func))
    } else if (typeof func === 'function') {
      return inner.concat(func(item))
    } else {
      return inner.concat(item)
    }
  }, [])
}

export function findSafety<T>(arr: T[], predicate: (v: T) => boolean, defaultIndex: number = 0) {
  const item = arr.find(predicate)

  if (!item) {
    return arr[defaultIndex]
  }

  return item
}

export const insertAtHead = <T>(targetArray: T[], item: T) => {
  return insertAt(targetArray, item, 0)
}

export const insertAt = <T>(targetArray: T[], item: T, index: number) => {
  targetArray.splice(index, 0, item)

  return targetArray
}

export const replaceAt = <T>(targetArray: T[], item: T, index: number) => {
  targetArray.splice(index, 1, item)

  return targetArray
}

export const removeAt = <T>(targetArray: T[], index: number) => {
  targetArray.splice(index, 1)

  return targetArray
}

export const range = (min: number, max: number, unit: number): number[] => {
  if (min > max) {
    return [max]
  }

  return min === max ? [min] : [min, ...range(min + unit, max, unit)]
}

export const groupBy = <T>(collection: T[], iteratee: (item: T) => any) => {
  return collection.reduce((result, value) => {
    const newKey = iteratee(value)
    if (Object.prototype.hasOwnProperty.call(result, newKey)) {
      result[newKey].push(value)
    } else {
      result[newKey] = [value]
    }

    return result
  }, {} as any)
}

export const fillArrayWithValue = (base: any[], val: string) => {
  const splitted = val.split('')

  return base.map((_: unknown, index: number) => {
    return splitted[index] ? splitted[index] : false
  })
}

export const toArray = <T>(anyValue: T): string[] => {
  return String(anyValue).split('')
}

export const reverseFlatten = <T>(arr: T[], unit: number) => {
  return [...Array(Math.ceil(arr.length / unit))].map(() => arr.splice(0, unit))
}

export const replaceElement = (arr: string[], start: number, end: number, marker: string = '*') => {
  return arr.map((el, index) => (index >= start && index <= end ? marker : el))
}

export const getMaskingRange = (formatStr: string) => {
  const format = formatStr.replace(/\s/g, '')
  const PIVOT = format.length
  let start = PIVOT
  let end = PIVOT

  format.split('').forEach((str, index) => {
    if (start === PIVOT && isNaN(Number(str))) {
      start = index
    }
    if (end === PIVOT && index > start && !isNaN(Number(str))) {
      end = index - 1
    }
  })

  if (end === PIVOT) {
    end = PIVOT - 1
  }

  if (start > end) {
    return { start: NaN, end: NaN }
  }

  return { start, end }
}

export const merge = <T>(base: T[], tobeMergedArray: []) => {
  const merged = [...base, ...tobeMergedArray]

  const filteredObject = merged.reduce(
    (acc: object, cur: any) => ({
      ...acc,
      [cur.itemId]: cur,
    }),
    {},
  )

  return Object.keys(filteredObject).map((id: any) => filteredObject[id])
}
